import styles from "./request-form.module.css";

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Flex, Typography, Card, Skeleton } from "antd";

import { getMe } from "@/api/auth";

import RequestModal from "@/components/modals/RequestModal";

import { getFormItem } from "@/api/requests";
import FlexibleRequestForm from "@/components/forms/FlexibleRequestForm";

const { Title, Text } = Typography;

const isValidEmail = (value) => {
  if (!value || typeof value !== "string") return false;

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const getValidUserEmail = (secondaryEmail, email) => {
  if (isValidEmail(secondaryEmail)) return secondaryEmail.trim();
  if (isValidEmail(email)) return email.trim();

  return "";
};

const ERROR_CONFIG = {
  403: {
    title: "Форма сейчас недоступна",
    subtitle:
      "Эта форма недоступна для заполнения. Возможно, она была скрыта или у вас нет необходимых прав доступа",
  },     
  404: {
    title: "Форма не найдена",
    subtitle: "Возможно, ссылка устарела или была удалена",
  },
  400: {
    title: "Некорректный запрос",
    subtitle: "Проверьте правильность ссылки",
  },
  500: {
    title: "Ошибка сервера",
    subtitle: "Попробуйте позже",
  },
};

const RequestForm = () => {
  const { uuid, id } = useParams();

  const [formData, setFormData] = useState(null);
  const [unitInfo, setUnitInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newRequest, setNewRequest] = useState(null);
  const [isModalShow, setIsModalShow] = useState(false);
  const [requestType, setRequestType] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    let requestTypeData = {};

    try {
      requestTypeData = await getFormItem({ uuid: uuid }, false);
      setRequestType(requestTypeData);

      const unit = requestTypeData?.units?.find(
        (item) => item.unit_uuid === id,
      );
      setUnitInfo(unit);
    } catch (error) {
      const status = error?.status;

      if (status === 403) {
        setError({ type: "403" });
      } else if (status === 404) {
        setError({ type: "404" });
      } else if (status === 400) {
        setError({ type: "400" });
      } else if (status >= 500) {
        setError({ type: "500" });
      } else {
        setError({
          type: "generic",
          message: error?.message || "Ошибка загрузки",
        });
      }

      setIsLoading(false);
      return;
    }

    try {
      const fetchedData = await getMe();
      const { email, secondary_email, lastname, firstname, patronymic, employee_uuid } =
        fetchedData.user;
      setFormData({
        user_email: getValidUserEmail(secondary_email, email),
        user_name:
          `${lastname || ""} ${firstname || ""} ${patronymic || ""}`.trim(),
        recipient_uuid: employee_uuid,
        unit_uuid: id,
        request_type_uuid: requestTypeData?.uuid ?? null,
      });
    } catch (error) {
      setFormData({
        user_email: "",
        user_name: "",
        recipient_uuid: null,
        unit_uuid: id,
        request_type_uuid: requestTypeData?.uuid ?? null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  const handleSave = async (request) => {
    if (request) {
      setNewRequest(request);
      setIsModalShow(true);
    }
  };

  const modalClose = () => {
    setIsModalShow(false);
    setNewRequest(null);

    window.close(); // Закрываем вкладку
  };

  return (
    <>
      <Flex className={styles.card_wrap}>
        <Card variant="borderless" className={styles.card}>
          <Flex vertical gap={25}>
            {isLoading ? (
              <Flex vertical align="center" className={styles.spin}>
                <Skeleton size="large" active />
              </Flex>
            ) : error ? (
              <Flex
                vertical
                align="center"
                justify="center"
                style={{ minHeight: 120, textAlign: "center" }}
              >
                {error.type !== "generic" ? (
                  <>
                    <Title
                      level={4}
                      type="secondary"
                      style={{ marginBottom: 8 }}
                    >
                      {ERROR_CONFIG[error.type]?.title}
                    </Title>
                    <Text type="secondary">
                      {ERROR_CONFIG[error.type]?.subtitle}
                    </Text>
                  </>
                ) : (
                  <Text type="danger">{error.message}</Text>
                )}
              </Flex>
            ) : (
              <>
                <Flex vertical gap={15}>
                  <Title level={5} style={{ margin: 0, textAlign: "end" }}>
                    {unitInfo?.unit_displayed_name}
                  </Title>
                  <Title level={2} style={{ margin: 0, textAlign: "center" }}>
                    {requestType?.name}
                  </Title>
                </Flex>
                <Text type="secondary">{unitInfo?.comment}</Text>
                <FlexibleRequestForm
                  onSave={handleSave}
                  data={{ ...formData, ...requestType }}
                />
              </>
            )}
          </Flex>
        </Card>
      </Flex>

      {isModalShow && (
        <RequestModal
          data={newRequest}
          visible={isModalShow}
          onCancel={modalClose}
        />
      )}
    </>
  );
};

export default RequestForm;
